import { useState, useMemo } from "react";
import { api, setAuthToken, API_URL } from "../api";

export default function Admin() {
  const [token, setToken] = useState("");
  const [credentials, setCredentials] = useState({ username: "", password: "" });
  const [message, setMessage] = useState("");
  const [contracts, setContracts] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [uploadFile, setUploadFile] = useState(null);

  // Contract Edit Modal State
  const [editingContract, setEditingContract] = useState(null);
  const [editForm, setEditForm] = useState({});

  // Filters
  const [search, setSearch] = useState("");
  const [sortField, setSortField] = useState("eventDate");
  const [sortAsc, setSortAsc] = useState(true);

  const filteredContracts = useMemo(() => {
    let list = [...contracts];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(
        (c) =>
          c.fullName.toLowerCase().includes(q) ||
          c.eventLocation.toLowerCase().includes(q) ||
          c.email?.toLowerCase().includes(q)
      );
    }
    list.sort((a, b) => {
      const va = a[sortField] ?? "";
      const vb = b[sortField] ?? "";
      if (va < vb) return sortAsc ? -1 : 1;
      if (va > vb) return sortAsc ? 1 : -1;
      return 0;
    });
    return list;
  }, [contracts, search, sortField, sortAsc]);

  async function login(e) {
    e.preventDefault();
    setMessage("");
    try {
      const res = await api.post("/auth/login", credentials);
      setToken(res.data.token);
      setAuthToken(res.data.token);
      await loadData(res.data.token);
    } catch {
      setMessage("Credenciales invalidas.");
    }
  }

  async function loadData() {
    const [cRes, pRes, qRes] = await Promise.all([
      api.get("/contracts"),
      api.get("/admin/photos"),
      api.get("/quotes"),
    ]);
    setContracts(cRes.data);
    setPhotos(pRes.data);
    setQuotes(qRes.data);
  }

  function logout() {
    setToken("");
    setAuthToken(null);
    setContracts([]);
    setPhotos([]);
    setQuotes([]);
  }

  async function uploadPhoto() {
    if (!uploadFile) {
      setMessage("Selecciona un archivo primero.");
      return;
    }
    try {
      const fd = new FormData();
      fd.append("file", uploadFile);
      await api.post("/admin/photos", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadFile(null);
      setMessage("Foto subida correctamente.");
      await loadData();
    } catch (err) {
      setMessage("Error al subir foto: " + (err.response?.data?.message || err.message));
    }
  }

  async function deletePhoto(id) {
    await api.delete(`/admin/photos/${id}`);
    await loadData();
  }

  async function downloadExcel() {
    const res = await api.get("/contracts/excel", { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a");
    a.href = url;
    a.download = "contratos.xlsx";
    a.click();
    URL.revokeObjectURL(url);
  }

  async function purge() {
    if (!window.confirm("Se eliminaran contratos con fecha de evento vencida. Continuar?")) return;
    const res = await api.delete("/contracts/purge-expired");
    setMessage(`Se eliminaron ${res.data.removed} contratos vencidos.`);
    await loadData();
  }

  async function updateQuoteStatus(id, status) {
    await api.patch(`/quotes/${id}/status`, { status });
    await loadData();
  }

  async function deleteQuote(id) {
    if (!window.confirm("¿Eliminar esta cotización?")) return;
    await api.delete(`/quotes/${id}`);
    await loadData();
  }

  function toggleSort(field) {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(true);
    }
  }

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return <span className="ml-1">{sortAsc ? "▲" : "▼"}</span>;
  };

  // Edit Contract Functions
  function openEditModal(contract) {
    setEditingContract(contract);
    setEditForm({ ...contract });
  }

  function closeEditModal() {
    setEditingContract(null);
    setEditForm({});
  }

  async function handleUpdateContract(e) {
    e.preventDefault();
    try {
      await api.put(`/admin/contracts/${editingContract.id}`, editForm);
      setMessage("Contrato actualizado exitosamente.");
      closeEditModal();
      await loadData();
    } catch (err) {
      setMessage("Error al actualizar contrato: " + (err.response?.data?.message || err.message));
    }
  }

  function handleEditFormChange(e) {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  }

  if (!token) {
    return (
      <section className="mx-auto max-w-md rounded-2xl border border-orange-400/20 bg-black/20 p-8">
        <h1 className="text-2xl font-bold uppercase tracking-wide">Acceso Administracion</h1>
        {message && <p className="mt-3 text-sm text-red-300">{message}</p>}
        <form className="mt-6 space-y-4" onSubmit={login}>
          <input
            className="w-full rounded-lg border border-orange-400/20 bg-black/30 p-3 placeholder-orange-200/40 focus:border-orange-400 focus:outline-none"
            placeholder="Usuario"
            value={credentials.username}
            onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
            required
          />
          <input
            className="w-full rounded-lg border border-orange-400/20 bg-black/30 p-3 placeholder-orange-200/40 focus:border-orange-400 focus:outline-none"
            type="password"
            placeholder="Contrasena"
            value={credentials.password}
            onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
            required
          />
          <button className="w-full rounded-lg bg-gradient-to-r from-orange-500 to-amber-400 px-6 py-3 font-bold text-black" type="submit">
            Iniciar Sesion
          </button>
        </form>
      </section>
    );
  }

  return (
    <section className="space-y-8">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-bold uppercase tracking-wide">Panel de Administracion</h1>
        <button className="rounded-lg border border-orange-300/30 px-4 py-2 text-sm hover:bg-orange-500/10" onClick={logout}>
          Cerrar Sesion
        </button>
      </div>

      {message && <p className="rounded-xl border border-orange-500/40 bg-black/30 p-3 text-sm">{message}</p>}

      {/* Actions */}
      <div className="flex flex-wrap gap-3">
        <button className="rounded-lg bg-orange-500 px-4 py-2 font-semibold text-black" onClick={downloadExcel}>
          Descargar Excel
        </button>
        <button className="rounded-lg border border-red-400/40 px-4 py-2 text-red-300 hover:bg-red-500/10" onClick={purge}>
          Depurar Vencidos
        </button>
      </div>

      {/* Contracts table with filters */}
      <div className="rounded-2xl border border-orange-400/20 bg-black/20 p-6">
        <div className="mb-4 flex flex-wrap items-center gap-4">
          <h2 className="font-bold uppercase">Contratos ({filteredContracts.length})</h2>
          <input
            className="ml-auto rounded-lg border border-orange-400/20 bg-black/30 px-3 py-2 text-sm placeholder-orange-200/40 focus:border-orange-400 focus:outline-none"
            placeholder="Buscar titular, email, ubicacion..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <div className="overflow-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-orange-500/10 text-xs uppercase">
              <tr>
                <th className="cursor-pointer p-3" onClick={() => toggleSort("fullName")}>
                  Titular<SortIcon field="fullName" />
                </th>
                <th className="cursor-pointer p-3" onClick={() => toggleSort("eventDate")}>
                  Fecha Evento<SortIcon field="eventDate" />
                </th>
                <th className="p-3">Ubicacion</th>
                <th className="cursor-pointer p-3" onClick={() => toggleSort("totalAmount")}>
                  Total<SortIcon field="totalAmount" />
                </th>
                <th className="p-3">Anticipo</th>
                <th className="p-3">Saldo</th>
                <th className="p-3">Pago</th>
                <th className="p-3">Notas Adicionales</th>
                <th className="p-3">Acciones</th>
                <th className="p-3">PDF</th>
              </tr>
            </thead>
            <tbody>
              {filteredContracts.map((c) => (
                <tr key={c.id} className="border-t border-orange-300/10 hover:bg-orange-500/5">
                  <td className="p-3 font-medium">{c.fullName}</td>
                  <td className="p-3">{new Date(c.eventDate).toLocaleDateString("es-MX")}</td>
                  <td className="p-3">{c.eventLocation}</td>
                  <td className="p-3">${c.totalAmount?.toLocaleString()}</td>
                  <td className="p-3">${c.advanceAmount?.toLocaleString() || 0}</td>
                  <td className="p-3">${((c.totalAmount || 0) - (c.advanceAmount || 0)).toLocaleString()}</td>
                  <td className="p-3">${c.paymentMethod}</td>
                  <td className="p-3 max-w-xs truncate" title={c.notes}>{c.notes || "-"}</td>
                  <td className="p-3">
                    <button
                      className="rounded bg-blue-500 px-3 py-1 text-xs font-bold text-white hover:bg-blue-600"
                      onClick={() => openEditModal(c)}
                    >
                      Editar
                    </button>
                  </td>
                  <td className="p-3">
                    <a
                      className="text-orange-300 underline"
                      href={`${API_URL}/api/contracts/pdf/${c.validationCode}`}
                      target="_blank"
                      rel="noreferrer"
                    >
                      PDF
                    </a>
                  </td>
                </tr>
              ))}
              {filteredContracts.length === 0 && (
                <tr><td colSpan={8} className="p-4 text-center text-orange-100/50">Sin resultados</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Photos management */}
      <div className="rounded-2xl border border-orange-400/20 bg-black/20 p-6">
        <h2 className="mb-4 font-bold uppercase">Gestion de Fotos ({photos.length})</h2>
        <div className="mb-4 flex flex-wrap items-center gap-3">
          <input type="file" accept="image/*" onChange={(e) => setUploadFile(e.target.files?.[0] || null)} />
          <button className="rounded-lg bg-orange-500 px-4 py-2 font-semibold text-black" onClick={uploadPhoto}>
            Subir Foto
          </button>
        </div>
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
          {photos.map((p) => (
            <div key={p.id} className="group relative">
              <img
                src={`${API_URL}/images/${p.fileName}`}
                alt="foto"
                className="h-32 w-full rounded-lg border border-orange-300/20 object-cover"
              />
              <button
                className="absolute right-2 top-2 hidden rounded-full bg-red-600 px-2 py-0.5 text-xs font-bold text-white group-hover:block"
                onClick={() => deletePhoto(p.id)}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Quotes management */}
      <div className="rounded-2xl border border-orange-400/20 bg-black/20 p-6">
        <h2 className="mb-4 font-bold uppercase">Cotizaciones ({quotes.length})</h2>
        <div className="overflow-auto">
          <table className="min-w-full text-left text-sm">
            <thead className="bg-orange-500/10 text-xs uppercase">
              <tr>
                <th className="p-3">Nombre</th>
                <th className="p-3">Teléfono</th>
                <th className="p-3">Tipo Evento</th>
                <th className="p-3">Lugar</th>
                <th className="p-3">Fecha</th>
                <th className="p-3">Estatus</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {quotes.map((q) => (
                <tr key={q.id} className="border-t border-orange-300/10 hover:bg-orange-500/5">
                  <td className="p-3 font-medium">{q.fullName}</td>
                  <td className="p-3">{q.phone}</td>
                  <td className="p-3">{q.eventType}</td>
                  <td className="p-3">{q.location}</td>
                  <td className="p-3">{new Date(q.eventDate).toLocaleDateString("es-MX")}</td>
                  <td className="p-3">
                    <select
                      className={`rounded-lg px-3 py-1 text-xs font-bold ${
                        q.status === "Enviado"
                          ? "bg-green-500/20 text-green-300"
                          : "bg-yellow-500/20 text-yellow-300"
                      }`}
                      value={q.status}
                      onChange={(e) => updateQuoteStatus(q.id, e.target.value)}
                    >
                      <option value="EnProceso">En Proceso</option>
                      <option value="Enviado">Enviado</option>
                    </select>
                  </td>
                  <td className="p-3">
                    <button
                      className="rounded-full bg-red-600 px-2 py-0.5 text-xs font-bold text-white hover:bg-red-700"
                      onClick={() => deleteQuote(q.id)}
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))}
              {quotes.length === 0 && (
                <tr><td colSpan={7} className="p-4 text-center text-orange-100/50">Sin cotizaciones</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Contract Modal */}
      {editingContract && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 px-4">
          <div className="w-full max-w-md rounded-2xl border border-orange-400 bg-zinc-900 p-6 shadow-2xl">
            <h2 className="mb-4 text-xl font-bold text-orange-400">Editar Contrato</h2>
            <form onSubmit={handleUpdateContract} className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-orange-200">Nombre Titular</label>
                <input
                  name="fullName"
                  value={editForm.fullName || ""}
                  onChange={handleEditFormChange}
                  className="w-full rounded-lg border border-orange-400/20 bg-black/30 p-2 text-white placeholder-orange-200/40 focus:border-orange-400 focus:outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-orange-200">Email</label>
                <input
                  name="email"
                  type="email"
                  value={editForm.email || ""}
                  onChange={handleEditFormChange}
                  className="w-full rounded-lg border border-orange-400/20 bg-black/30 p-2 text-white placeholder-orange-200/40 focus:border-orange-400 focus:outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-orange-200">Teléfono</label>
                <input
                  name="phone"
                  value={editForm.phone || ""}
                  onChange={handleEditFormChange}
                  className="w-full rounded-lg border border-orange-400/20 bg-black/30 p-2 text-white placeholder-orange-200/40 focus:border-orange-400 focus:outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-orange-200">Fecha del Evento</label>
                <input
                  name="eventDate"
                  type="date"
                  value={editForm.eventDate?.split("T")[0] || ""}
                  onChange={handleEditFormChange}
                  className="w-full rounded-lg border border-orange-400/20 bg-black/30 p-2 text-white focus:border-orange-400 focus:outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-orange-200">Ubicación</label>
                <input
                  name="eventLocation"
                  value={editForm.eventLocation || ""}
                  onChange={handleEditFormChange}
                  className="w-full rounded-lg border border-orange-400/20 bg-black/30 p-2 text-white placeholder-orange-200/40 focus:border-orange-400 focus:outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-orange-200">Monto Total</label>
                <input
                  name="totalAmount"
                  type="number"
                  value={editForm.totalAmount || ""}
                  onChange={handleEditFormChange}
                  className="w-full rounded-lg border border-orange-400/20 bg-black/30 p-2 text-white placeholder-orange-200/40 focus:border-orange-400 focus:outline-none"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-orange-200">Anticipo</label>
                <input
                  name="advanceAmount"
                  type="number"
                  value={editForm.advanceAmount || ""}
                  onChange={handleEditFormChange}
                  className="w-full rounded-lg border border-orange-400/20 bg-black/30 p-2 text-white placeholder-orange-200/40 focus:border-orange-400 focus:outline-none"
                />
              </div>
               <div>
                <label className="block text-sm font-semibold text-orange-200">Método de Pago</label>
                 <select
                  name="paymentMethod"
                  value={editForm.paymentMethod || ""}
                  onChange={handleEditFormChange}
                  className="w-full rounded-lg border border-orange-400/20 bg-black/30 p-2 text-white focus:border-orange-400 focus:outline-none"
                  required
                >
                  <option value="">Seleccione</option>
                  <option value="Efectivo">Efectivo</option>
                  <option value="Transferencia">Transferencia</option>
                  <option value="Tarjeta">Tarjeta</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-semibold text-orange-200">Notas Adicionales</label>
                <textarea
                  name="notes"
                  value={editForm.notes || ""}
                  onChange={handleEditFormChange}
                  rows={3}
                  className="w-full rounded-lg border border-orange-400/20 bg-black/30 p-2 text-white placeholder-orange-200/40 focus:border-orange-400 focus:outline-none"
                  placeholder="Instrucciones especiales, peticiones de canciones, etc."
                />
              </div>

              <div className="mt-6 flex justify-end gap-3 border-t border-orange-500/20 pt-4">
                <button
                  type="button"
                  onClick={closeEditModal}
                  className="rounded-lg border border-orange-400/40 px-4 py-2 font-bold text-orange-300 hover:bg-orange-400/10"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="rounded-lg bg-orange-500 px-4 py-2 font-bold text-black hover:bg-orange-400"
                >
                  Guardar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </section>
  );
}
